import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Tooltip, IconButton, Typography, Fab } from '@material-ui/core';
import { Refresh, Add, Delete } from '@material-ui/icons';

import { BackButton } from '../../sc-utilities';
import ReportDataDelete from './ReportDataDelete';
import * as actions from './store/reportDataActions';
import * as action from '../../np-core/Auth/store/authActions';
import { hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import Toolbar from '../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../themes';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const ReportDataList = props => {
    const { classes, saveColumnsFilter, deleting, onFetchReportDataListCount, redirectHandler, fetching, onFetchReportDataList, onBulkDeleteReportData, activeCustomer, translations, tableColumns, permissions, tableRef } = props;
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const [deleteModalState, setDeleteModalState] = useState({ open: false, reportDataID: null, reportDataIDs: [] });

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['report_data_delete']);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            return { id: `checkbox_${rowData.ReportDataID}` };
        },
        search: false,
        minBodyHeight: window.innerHeight - 290 < 300 ? 300 : window.innerHeight - 290,
        maxBodyHeight: window.innerHeight - 290,
        pageSizeOptions: rowsPageSizeInit,

        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} Report Data`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(reportData => reportData.ReportDataID))
        }
    ];

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }


    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchReportDataListCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchReportDataList(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations["report_data"] || 'Report Data'}</Typography>
                {
                    hasPermission(permissions, 'report_data_create') ?
                        <Fab
                            id={'create_new_report_data'}
                            variant="extended"
                            size="small"
                            color="primary"
                            aria-label="Add"
                            className={classes.fab}
                            style={{ borderRadius: '1px' }}
                            onClick={() => redirectHandler(`/settings/report-data/create`)}
                        >
                            <Add size="small" />
                            {translations["create_new"] || "Create new"}
                        </Fab> : null
                }
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh id={'refresh_button'} />
                    </IconButton>
                </Tooltip>
            </div>
        </div>

    }

    const handleDelete = async () => {
        setDeleteModalState({ open: false, logoID: null, logoIDs: [], forBulk: false });
        return;
    };

    const handleBulkDelete = reportDataIDS => {
        setDeleteModalState({ ...deleteModalState, open: true, reportDataIDs: reportDataIDS, forBulk: true });
    };

    const handleCloseSafe = () => {
        handleDelete();
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteReportData({ ReportDataIDs: deleteModalState.reportDataIDs, ActiveCustomerID: activeCustomer });
        handleDelete();
        tableRef.current && tableRef.current.onQueryChange();
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`ReportData`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations} />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                nChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'report_data_delete') ?
                    <ReportDataDelete
                        translations={translations}
                        deleting={deleting}
                        activeCustomer={activeCustomer}
                        entityName={translations['report_data'] || 'Report Data'}
                        deleteModalState={deleteModalState}
                        handleCloseSafe={handleCloseSafe}
                        handleCloseDanger={handleCloseDanger}
                    /> : null
            }
        </Paper>
    );
}

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        deleting: state.setting.reportData.deleting,
        fetching: state.setting.reportData.fetching
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchReportDataListCount: filter => dispatch(actions.fetchReportDataCount(filter)),
        onFetchReportDataList: filter => dispatch(actions.fetchReportData(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkDeleteReportData: reportData => dispatch(actions.bulkDeleteReportData(reportData))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ReportDataList));